/**
 * The online/offline dot.
 *
 * An `Ellipse` in the XAML with an `OnlineBrush` converter, at six sites. A control here so the
 * colour is resolved from the theme at paint time rather than by a converter that returns a brush
 * somebody else is holding - the rule the whole palette is built on.
 *
 * Worth being a control rather than a chip: a status word repeated down a list of forty machines is
 * forty words to read, and the thing a technician is scanning for is the one row that is a different
 * colour from the rest.
 */
import { Control, type ControlOptions, type RenderContext, type Theme } from 'webwin';
import { crm } from '../theme/CrmTheme.js';

export type DotTone = 'online' | 'offline' | 'warn' | 'danger' | 'info' | 'muted';

export interface DotOptions extends ControlOptions {
  tone?: DotTone;
  /** Diameter. 12 in a list row, 10 beside a heading - the two the design uses. */
  size?: number;
}

export class Dot extends Control {
  tone: DotTone;

  constructor(options: DotOptions = {}) {
    const size = options.size ?? 12;
    super({ width: size, height: size, ...options });
    this.tone = options.tone ?? 'muted';
  }

  /** A decoration, never a hit target: a dot inside a clickable card must not swallow the click. */
  protected override hitTestSelf(): boolean {
    return false;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    const fill =
      this.tone === 'online'
        ? c.ok
        : this.tone === 'warn'
          ? c.warn
          : this.tone === 'danger'
            ? c.danger
            : this.tone === 'info'
              ? c.info
              : this.tone === 'offline'
                ? c.mutedText
                : c.border;

    const r = this.localRect;
    // A circle is a rounded rect at half its own size, which is what the SDF pipeline already draws.
    rc.drawRect(r, { fill, radius: Math.min(r.width, r.height) / 2 });
  }
}

/** `dot(true)` for the common case: a presence flag straight from a DTO. */
export function dot(online: boolean, options: DotOptions = {}): Dot {
  return new Dot({ tone: online ? 'online' : 'offline', vAlign: 'center', ...options });
}
